import { useCallback, useEffect, useRef, useState } from 'react';
import {
    fetchSessionShareQuestionsWithWorker,
    fetchSessionShareSummary,
    SessionShareSummary,
} from '../../../api/publicSessionApi';
import {
    SessionQuestionEntityFrontend,
    WorkerDtoFrontend,
} from '../../../api/sessionQuestionsApi';
import logger from '../../../utils/logger';

export interface PublicShareSessionState {
    loading: boolean;
    error: string | null;
    sessionSummary: SessionShareSummary | null;
    worker: WorkerDtoFrontend | null;
    questions: SessionQuestionEntityFrontend[];
    /** Re-fetch the form data. Pass `silent: true` to suppress the loading spinner. */
    reload: (options?: { silent?: boolean }) => Promise<void>;
    /**
     * Replace the worker + questions in place from data we already have in
     * hand (e.g. the response from /translate-questions, which returns the
     * same wrapped shape). Avoids a second GET to the backend when the
     * caller already holds the full payload.
     */
    injectWrapped: (wrapped: {
        worker: WorkerDtoFrontend;
        questions: SessionQuestionEntityFrontend[];
    }) => void;
}

/**
 * Fetches the public share summary + the worker-wrapped question list. The
 * orchestrator binds the returned `reload` to UI affordances; the rest of the
 * tree just reads `questions` / `worker` / `sessionSummary` as state.
 *
 * Stale-response guard: every `reload` call bumps a token. When the awaited
 * `Promise.all` resolves, we compare the token captured at call time against
 * the current ref — if they differ (because the publicShareId changed or
 * another reload kicked off mid-flight), we drop the result on the floor.
 * Prevents cross-session data leaks during rapid deep-link navigation.
 */
export const usePublicShareSession = (
    publicShareId: string | undefined,
): PublicShareSessionState => {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [sessionSummary, setSessionSummary] = useState<SessionShareSummary | null>(null);
    const [worker, setWorker] = useState<WorkerDtoFrontend | null>(null);
    const [questions, setQuestions] = useState<SessionQuestionEntityFrontend[]>([]);
    const requestTokenRef = useRef<number>(0);

    const reload = useCallback(
        async (options?: { silent?: boolean }) => {
            if (!publicShareId) {
                setError('Invalid or missing share identifier.');
                if (!options?.silent) {
                    setLoading(false);
                }
                return;
            }

            const myToken = ++requestTokenRef.current;

            if (!options?.silent) {
                setLoading(true);
            }
            setError(null);
            try {
                const [summary, wrapped] = await Promise.all([
                    fetchSessionShareSummary(publicShareId),
                    fetchSessionShareQuestionsWithWorker(publicShareId),
                ]);
                if (requestTokenRef.current !== myToken) {
                    // A newer reload superseded us — bail before touching state
                    // so we don't paint stale data over fresh data.
                    return;
                }
                setSessionSummary(summary);
                setWorker(wrapped.worker);
                setQuestions(wrapped.questions);
            } catch (err: any) {
                if (requestTokenRef.current !== myToken) {
                    return;
                }
                logger.error('Failed to load public session form', err);
                const message =
                    err?.response?.data?.message ||
                    err?.message ||
                    'Unable to load the form. Please try again later.';
                setError(message);
            } finally {
                if (requestTokenRef.current === myToken && !options?.silent) {
                    setLoading(false);
                }
            }
        },
        [publicShareId],
    );

    useEffect(() => {
        void reload();
    }, [reload]);

    const injectWrapped = useCallback(
        (wrapped: { worker: WorkerDtoFrontend; questions: SessionQuestionEntityFrontend[] }) => {
            setWorker(wrapped.worker);
            setQuestions(wrapped.questions);
        },
        [],
    );

    return { loading, error, sessionSummary, worker, questions, reload, injectWrapped };
};
